import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl, FormArray, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NavigationActionButton } from 'src/app/components/navigation-bar/models/navigation-action-button.model';
import { ConfigurationProfile, DigitalLogFormAlias, JobDigitalLogOnDemandForm, JobDigitalLogOnDemandFormPriorAliases, DigitalLogProfileGroup, DigitalLogProfileGroupDetail, JobDigitalLogFormRequest, ResourceAttributeDefinition } from 'src/app/models/digital-log/digital-log.model';
import { DigitalLogService } from 'src/app/services/sections/job/digital-log.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import moment, { Moment } from 'moment';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { ToastType } from 'src/app/components/toast/models/toast-type.enum';
import { Toast } from 'src/app/components/toast/models/toast.model';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ButtonIcons } from 'src/app/components/action-button/models/button-icons.enum';
import { GenericModalComponent } from 'src/app/components/generic-modal/generic-modal.component';
import { GlobalUtilsService } from 'src/app/services/ui/global-utils.service';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { SiteService } from 'src/app/services/site.service';
import { Item } from 'src/app/models/item.model';
import { DynamicAttribute } from '../../dynamic-controls/dynamic-attribute';
import { AttributeType } from '../../dynamic-controls/models/attribute-type.enum';
import { AttributeDefinition } from '../../dynamic-controls/models/attribute-definition';
import { ComponentTypeProvider } from '../../dynamic-controls/component-type-provider';
import { FormGroupComponent } from '../../dynamic-controls/form-group/form-group.component';

@Component({
	selector: 'digital-log-on-demand-add',
	templateUrl: './digital-log-on-demand-add.component.html',
	styleUrls: ['./digital-log-on-demand-add.component.scss', '../../../stylesheets/job-main.scss', '../../../stylesheets/job-actions.scss', '../../../stylesheets/job-inputs.scss', '../../../stylesheets/job-form.scss']
})
export class DigitalLogOnDemandAddComponent implements OnInit, OnDestroy {
	@ViewChild('addAliasModal') public addAliasModal: GenericModalComponent;
	@ViewChild('editAssetModal') public editAssetModal: GenericModalComponent;
	@ViewChild('groupListModal') public groupListModal: GenericModalComponent;
	@ViewChild('deleteGroupConfirmationModal') public deleteGroupConfirmationModal: GenericModalComponent;
	@ViewChild('editGroupNameModal') public editGroupNameModal: GenericModalComponent;
	@ViewChild('saveEmptyGroupConfirmationModal') public saveEmptyGroupConfirmationModal: GenericModalComponent;
	@ViewChild('addGroupModal') public addGroupModal: GenericModalComponent;
	@ViewChild('deleteLastGroupModal') public deleteLastGroupModal: GenericModalComponent;

	public navigationBarActions: Map<string, NavigationActionButton>;

	public moment = moment;
	public onDemandFormGroup: FormGroup;
	public editAssetFormGroup: FormGroup;
	public readonly ButtonIcons = ButtonIcons;
	public aliasesFormGroup: FormGroup;
	public isLoading = false;
	public isSaving = false;
	public digitalLogFormAliases: DigitalLogFormAlias[];
	public selectedOnDemandForm: JobDigitalLogOnDemandForm;
	public onDemandForms: JobDigitalLogOnDemandForm[];
	public aliasesToSave: DigitalLogFormAlias[] = [];
	public onDemandFormsToCreate: JobDigitalLogOnDemandForm[] = [];
	public isMobileDevice: boolean;
	public isSetupMode: boolean;

	public isLoadingAssetSubTypes: boolean = false;
	public assetSubTypes: Item<number, string>[] = [];

	public isConfigurationView: boolean = false;
	public canConfigureRounds: boolean = false;
	public configurationProfile: ConfigurationProfile[] = [];
	public digitalLogProfileGroups: DigitalLogProfileGroupDetail[] = [];
	public selectedProfileGroup: DigitalLogProfileGroup;
	public defaultWorkProfile: DigitalLogProfileGroup = <DigitalLogProfileGroup>{ name: 'Default Scope', digitalLogProfileGroupId: 0 };
	public groupForm: FormGroup;
	public addGroupForm: FormGroup;
	public otherAttrDefinitions: ResourceAttributeDefinition[] = [];
	public dynAttributes: DynamicAttribute[] = [];
	private attrDefsLoaded = false;
	
	private unsubscriber$ = new Subject<void>();
	private saveFormSubject = new Subject<any>();

	get name() { return this.editAssetFormGroup.get('name'); }

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private digitalLogService: DigitalLogService,
		private formBuilder: FormBuilder,
		private siteService: SiteService,
		private componentTypeProvider: ComponentTypeProvider,
	) { }

	ngOnInit(): void {
		this.navigationBarActions = GlobalSettingsService.navigationBarActions;
		this.isMobileDevice = GlobalUtilsService.isMobileDevice;

		GlobalSettingsService.navigationBarActions = new Map<string, NavigationActionButton>();
		this.injectActionButtons();

		this.onDemandForms = this.activatedRoute.snapshot.data.onDemandForms;
		this.isConfigurationView = this.activatedRoute.snapshot.data.isConfigurationView as boolean ?? false;
		this.digitalLogProfileGroups = this.activatedRoute.snapshot.data.profileGroups as DigitalLogProfileGroup[] ?? [];
		if (this.digitalLogProfileGroups.length === 0) {
			this.digitalLogProfileGroups.push(this.defaultWorkProfile);
		}
		this.selectedProfileGroup = this.digitalLogProfileGroups.length > 0
			? this.digitalLogProfileGroups[0]
			: this.defaultWorkProfile;

		if(this.isConfigurationView) {
			this.canConfigureRounds = this.activatedRoute.snapshot.data.canConfigureRounds as boolean ?? false;
			this.configurationProfile = this.activatedRoute.snapshot.data.configurationProfile as ConfigurationProfile[] ?? [];
		}
		this.initializeFormGroups();
		this.loadOnDemandForms(this.onDemandForms);

		this.saveFormSubject.pipe(
			debounceTime(800),
			switchMap(() => {
				const configuration = this.buildConfiguration(this.forms.getRawValue());

				this.isSaving = true;

				return this.digitalLogService.updateConfigurationProfile(TokenHelper.getToken(this.activatedRoute),
					configuration,
					this.selectedProfileGroup.digitalLogProfileGroupId,
					this.selectedProfileGroup.name
				);
			}),
			takeUntil(this.unsubscriber$)
		).subscribe({
			next: (groupId) => {
				this.isSaving = false;
				ToastService.pushToast({
					icon: 'check_circle',
					text: 'Configuration saved successfully.',
					type: ToastType.Success,
					timeout: 2000,
				} as Toast);
				// if current profile group is the default one, replace its groupId with the created one
				if (this.selectedProfileGroup.digitalLogProfileGroupId == this.defaultWorkProfile.digitalLogProfileGroupId) {
					this.selectedProfileGroup = { 
						...this.defaultWorkProfile, 
						digitalLogProfileGroupId: groupId,
					 }
					 // remove default profile from the list and add the newly created one
					 this.digitalLogProfileGroups = this.digitalLogProfileGroups.filter(g => g.digitalLogProfileGroupId != this.defaultWorkProfile.digitalLogProfileGroupId);
					 this.getDigitalLogProfileGroups();
				}
			},
			error: (e) => {
				this.isSaving = false;
				console.error('Error saving configuration:', e);
				ToastService.pushToast({
					icon: 'error',
					text: 'Error saving configuration.',
					type: ToastType.Error,
				} as Toast);
			}
		});

		GlobalSettingsService.isSecondaryLoaderLoading = false;
		GlobalSettingsService.isGlobalLoaderLoading = false;
	}

	get title(): string {
		return this.isConfigurationView ? 'Configuration' : 'On-Demand Forms';
	}

	ngOnDestroy(): void {
		GlobalSettingsService.navigationBarActions = this.navigationBarActions;
		if (this.unsubscriber$) {
			this.unsubscriber$.next();
			this.unsubscriber$.complete();
		}
	}

	private injectActionButtons(): void {
		GlobalSettingsService.navigationBarActions.set(
			'exit_onDemand_form',
			{
				action: () => this.router.navigate(
					['../'], { relativeTo: this.activatedRoute }
				),
				text: 'Exit Form',
				isInDropdownMenu: false,
				isTextOnlyShownInDropdown: true,
				icon: 'close',
				listOrder: 0
			});
	}

	get aliases(): FormArray {
		// tslint:disable-next-line:no-string-literal
		return this.aliasesFormGroup.controls['aliases'] as FormArray;
	}

	get forms(): FormArray {
		// tslint:disable-next-line:no-string-literal
		return this.onDemandFormGroup.controls['forms'] as FormArray;
	}

	public getDigitalLogForm(digitalLogFormId: number): JobDigitalLogOnDemandForm {
		const form = this.onDemandForms.find(x => x.digitalLogFormId === digitalLogFormId);
		if (form?.resourceTypeId) { this.getAssetsubTypes(form?.resourceTypeId); }
		return form;
	}

	public initializeFormGroups(): void {
		this.aliasesFormGroup = new FormGroup({
			aliases: this.formBuilder.array([])
		});

		this.onDemandFormGroup = new FormGroup({
			forms: new FormArray([])
		});

		this.onDemandFormGroup.valueChanges.pipe(
			debounceTime(1000),
			takeUntil(this.unsubscriber$)
		).subscribe(() => {
			this.handleOnDemandFormGroupValueChanges();
		});

		this.editAssetFormGroup = new FormGroup({
			subType: new FormControl(0),
			name: new FormControl(null, [Validators.required, Validators.minLength(2), this.checkAssetDuplicateName()]),
			identifier: new FormControl(),
			make: new FormControl(),
			model: new FormControl(),
			description: new FormControl()
		});

		this.groupForm = new FormGroup({
			name: new FormControl(null, [Validators.required, Validators.minLength(2), this.checkGroupDuplicateName()]),
		});

		this.addGroupForm = new FormGroup({
			name: new FormControl(null, [Validators.required, Validators.minLength(2), this.checkGroupDuplicateName()])
		});
	}

	private handleOnDemandFormGroupValueChanges(): void {
		// update the forms count in the selected profile group only if necessary
		const group = this.digitalLogProfileGroups.find(x => x.digitalLogProfileGroupId === this.selectedProfileGroup.digitalLogProfileGroupId);
		if (group) {
			const includedCount = this.forms.controls.reduce((count, f) => count + (f.get('isIncluded').value ? 1 : 0), 0);
			if (group.formsCount !== includedCount) {
				group.formsCount = includedCount;
			}
		}
	}

	private crtExistsSameAssetType: boolean = false;
	private crtAccountSiteResourceId?: number;

	public loadAliases(aliases: DigitalLogFormAlias[], data: FormGroup) {
		const includedIds = data.get('includedAliasesIds').value;
		this.crtAccountSiteResourceId = data.value.accountSiteResourceId;
		this.crtExistsSameAssetType = !!this.crtAccountSiteResourceId ? this.existsSameAssetType(this.crtAccountSiteResourceId, aliases) : false;
		aliases.forEach((item) => {
			this.aliases.push(new FormGroup({
				accountSiteResourceId: new FormControl(item?.accountSiteResourceId),
				isIncluded: new FormControl({ value: includedIds.some(x => x === item.accountSiteResourceId), disabled: !item.isActive || (this.crtExistsSameAssetType && this.crtAccountSiteResourceId != item?.accountSiteResourceId) }),
				alias: new FormControl(item?.alias),
				aliasIdentifier: new FormControl(item?.aliasIdentifier),
				lastUpdatedOn: new FormControl(item?.lastCollectedOn),
				isActive: new FormControl(item?.isActive),
				canBeIncluded: new FormControl(!this.crtExistsSameAssetType || this.crtAccountSiteResourceId === item?.accountSiteResourceId),
				resourceTypeId: new FormControl(item?.resourceTypeId),
				resourceSubType: new FormControl(item?.resourceSubType),
				resourceSubTypeId: new FormControl(item?.resourceSubTypeId),
				make: new FormControl(item?.make ?? null),
				model: new FormControl(item?.model ?? null),
				description: new FormControl(item?.description ?? null)
			}));
		});
	}

	private existsSameAssetType(accountSiteResourceId: number, aliases: DigitalLogFormAlias[]): boolean {
		if (!accountSiteResourceId || !aliases || aliases.length == 0) { return false; }

		return aliases.some(a => a.accountSiteResourceId === accountSiteResourceId);
	}

	public loadOnDemandForms(forms: JobDigitalLogOnDemandForm[]): void {
		this.forms.clear();
		forms.forEach((item) => {
			const group = new FormGroup({
				isIncluded: new FormControl(this.isConfigurationView && this.configurationProfile.some(x => x.digitalLogFormId === item.digitalLogFormId) ? true : false),
				digitalLogFormId: new FormControl(item.digitalLogFormId),
				formName: new FormControl(item.formName),
				lastCollectedOn: new FormControl(item.lastCollectedOn),
				accountSiteId: new FormControl(item.accountSiteId),
				accountSiteResourceId: new FormControl(item?.accountSiteResourceId),
				resourceTypeId: new FormControl(item.resourceTypeId),
				includedAliasesIds: new FormArray(
					this.isConfigurationView 
						? (this.configurationProfile.find(x => x.digitalLogFormId === item.digitalLogFormId)?.accountSiteResourceIds?.map(assetId => new FormControl(assetId)) ?? []) 
						: []
				),
				hasNoLastCollectedDate: new FormControl(!item.digitalLogFormAliases.some(x => x.lastCollectedOn))
			});

			if (item.resourceTypeId) {
				group.get('includedAliasesIds').setValidators(Validators.required);
			}

			this.forms.push(
				group
			);
		});
	}

	public addOnDemandForms(): void {
		GlobalSettingsService.isSecondaryLoaderLoading = true;
		const selectedForms = (this.forms.getRawValue() as JobDigitalLogOnDemandForm[]).filter(x => x.isIncluded);

		this.digitalLogService.addOnDemandDigitalLogFormToJob(TokenHelper.getToken(this.activatedRoute), selectedForms).subscribe(res => {
			this.router.navigate(['../'], { relativeTo: this.activatedRoute });
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		}, () => {
			ToastService.pushToast({
				icon: 'error',
				text: 'Add could not be completed!',
				type: ToastType.Error
			} as Toast);
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		});
	}

	public saveAliases(): void {
		this.isSetupMode = false;
	}

	public onModalClose(): void {
		this.addAliasModal.hide();
		this.resetAliases();
	}

	public resetAliases(): void {
		this.aliasesToSave = [];
		this.selectedOnDemandForm = null;
		this.aliases.clear();
	}

	public onOnDemandCheckChange(form: FormGroup, e: any): void {
		if (e.checked) {
			if (form.get('resourceTypeId').value && form.get('includedAliasesIds').value.length === 0) {
				this.onMultipleAliasesClick(form);
				this.addAliasModal.open({ data: form });
				form.get('isIncluded').patchValue(false);
			}
			else {
				if (this.isConfigurationView)
					this.triggerSaveForm();
			}
		} else {
			(form.get('includedAliasesIds') as FormArray).clear();

			if (this.isConfigurationView)
				this.triggerSaveForm();
		}
	}

	public onAliasIncludeCheckChange(alias: FormGroup, data: FormGroup, event: any): void {
		if (event.checked) {
			(data.get('includedAliasesIds') as FormArray)
				.push(new FormControl(alias.get('accountSiteResourceId').value));
		} else {
			const index = data.get('includedAliasesIds').value.findIndex(id => id === alias.get('accountSiteResourceId').value);
			(data.get('includedAliasesIds') as FormArray).removeAt(index);
		}
		const value = [...data.get('includedAliasesIds').value];
		data.patchValue({ isIncluded: value.length > 0 });

		if (this.isConfigurationView)
			this.triggerSaveForm();
	}

	public onAliasActiveCheckChange(alias: FormGroup, data: FormGroup, event: any): void {
		event.preventDefault();

		const asset = alias.value;
		asset.isActive = !alias.value.isActive;
		this.saveAsset(asset, true, data);
	}

	public onMultipleAliasesClick(data: FormGroup): void {
		this.selectedOnDemandForm = this.getDigitalLogForm(data.get('digitalLogFormId').value);
		this.loadAliases(this.selectedOnDemandForm.digitalLogFormAliases, data);
		this.digitalLogFormAliases = this.selectedOnDemandForm.digitalLogFormAliases;
	}

	public getDigitalLogFormAliases(form: JobDigitalLogOnDemandForm): void {
		this.selectedOnDemandForm = form;
		this.isLoading = true;
		this.digitalLogService.getDigitalLogFormAliases(TokenHelper.getToken(this.activatedRoute), form).subscribe(res => {
			this.digitalLogFormAliases = res;
			this.isLoading = false;
		}, error => {
			ToastService.pushToast({
				icon: 'error',
				text: 'Could not get aliases. Try again!',
				type: ToastType.Error
			} as Toast);
			this.isLoading = false;
		});
	}

	public onEditAsset(item?): void {
		let asset = {
			digitalLogFormId: this.selectedOnDemandForm.digitalLogFormId,
			accountSiteId: this.selectedOnDemandForm.accountSiteId,
			accountSiteResourceId: null,
			alias: null,
			lastUpdatedOn: null,
			isActive: true,
			isIncluded: false,
			aliasIdentifier: null,
			resourceTypeId: this.selectedOnDemandForm?.resourceTypeId,
			resourceType: this.selectedOnDemandForm?.resourceType,
			resourceSubTypeId: item ? item?.resourceSubTypeId : this.selectedOnDemandForm?.resourceSubTypeId,
			resourceSubType: item ? item?.resourceSubType : this.selectedOnDemandForm?.resourceSubType
		} as DigitalLogFormAlias;

		if (item && item.value) {
			asset = { ...asset, ...item.value };
		}
		this.editAssetFormGroup.patchValue({
			subType: asset?.resourceSubTypeId || 0, name: asset.alias, identifier: asset.aliasIdentifier,
			make: (item?.value?.make ?? item?.make ?? asset.make ?? null), model: (item?.value?.model ?? item?.model ?? asset.model ?? null),
			description: (item?.value?.description ?? item?.description ?? asset.description ?? null),
		});
		const aliasFromList = this.selectedOnDemandForm?.digitalLogFormAliases
			?.find(a => a.accountSiteResourceId === asset.accountSiteResourceId);

		const attrs = aliasFromList?.attributes ?? [];
		this.editAssetModal.open(asset);
		setTimeout(() => this.prefillAttrControls(attrs), 0);
	}

	public onSetupClick(): void {
		if (!this.selectedOnDemandForm?.canConfigureAssets) {
			return;
		}
		this.isSetupMode = true;
		const rtId = this.selectedOnDemandForm?.resourceTypeId;
		if (!rtId) {
			this.otherAttrDefinitions = [];
			this.dynAttributes = [];
			return;
		}

		if (this.attrDefsLoaded) return;

		this.isLoading = true;
		this.digitalLogService
			.getResourceAttributeDefinitions(TokenHelper.getToken(this.activatedRoute), rtId, false, false).subscribe({
				next: defs => {
					this.otherAttrDefinitions = defs ?? [];
					this.dynAttributes = (defs ?? [])
						.map(dto => {
							const formControlName = `attr_${dto.id}`;
							const def = new AttributeDefinition(dto as any, formControlName);
							const cmp = this.componentTypeProvider.getComponent(dto.attributeTypeId as AttributeType);
							return cmp ? new DynamicAttribute(cmp, def) : null;
						})
						.filter(Boolean) as DynamicAttribute[];

					this.attrDefsLoaded = true;
					this.isLoading = false;
				},
				error: () => {
					ToastService.pushToast({
						icon: 'error',
						text: 'Could not load attribute definitions.',
						type: ToastType.Error
					} as Toast);
					this.isLoading = false;
				}
			});
	}

	public onSelectAliasesAllClick(data: any): void {
		if (this.aliases.controls.length === 0) {
			return;
		}
		let activeAliases = this.aliases.controls.filter(c => c.get('isActive').value && c.get('canBeIncluded').value && c.get('resourceTypeId').value === data.get('resourceTypeId').value);

		activeAliases.forEach(element => {
			element.get('isIncluded').patchValue(true);
			if ((!(data.get('includedAliasesIds') as FormArray).controls
				.some(p => p.value === element.get('accountSiteResourceId').value))) {
				(data.get('includedAliasesIds') as FormArray)
					.push(new FormControl(element.get('accountSiteResourceId').value));
			}
		});

		const value = [...data.get('includedAliasesIds').value];
		data.patchValue({ isIncluded: value.length > 0 });
		
		if (this.isConfigurationView) {
			this.triggerSaveForm();
		}
	}

	private applySelectAliasesPriorClick(data: any, aliases: JobDigitalLogOnDemandFormPriorAliases[]): void {
		this.onClearAliasesSelectionClick(data);
		if (this.aliases.controls.length === 0) {
			return;
		}
		let activeAliases = this.aliases.controls.filter(c => c.get('isActive').value && c.get('canBeIncluded').value);
		var digitalLogFormId = data.get('digitalLogFormId').value;
		var priorValues = aliases.find(r => r.digitalLogFormId === digitalLogFormId);

		if (!priorValues) {
			return;
		}

		activeAliases.forEach(element => {
			if (priorValues?.digitalLogFormAliases.includes(element.get('accountSiteResourceId').value)) {
				element.get('isIncluded').patchValue(true);
				if ((!(data.get('includedAliasesIds') as FormArray).controls
					.some(p => p.value === element.get('accountSiteResourceId').value))) {
					(data.get('includedAliasesIds') as FormArray)
						.push(new FormControl(element.get('accountSiteResourceId').value));
				}
			}
		});

		const value = [...data.get('includedAliasesIds').value];
		data.patchValue({ isIncluded: value.length > 0 });
	}

	public onSelectAliasesPriorClick(data: any): void {
		var digitalLogFormId = data.get('digitalLogFormId').value;
		this.isLoading = true;
		this.digitalLogService.getOnDemandDLFormsPriorInspection(TokenHelper.getToken(this.activatedRoute), digitalLogFormId).subscribe({
			next: (response: JobDigitalLogOnDemandFormPriorAliases[]) => {
				this.isLoading = false;
				this.applySelectAliasesPriorClick(data, response);
			},
			error: error => {
				ToastService.pushToast({
					icon: 'error',
					text: 'Could not add Alias. Try again!',
					type: ToastType.Error
				} as Toast);
				this.isLoading = false;
			}
		});
	}

	public onClearAliasesSelectionClick(data: any): void {
		if (this.aliases.controls.length === 0) 
			return;

		let activeAliases = this.aliases.controls.filter(c => c.get('isActive').value);
		activeAliases.forEach(element => {
			element.get('isIncluded').patchValue(false);
			const index = data.get('includedAliasesIds').value.findIndex(id => id === element.get('accountSiteResourceId').value);
			if (index >= 0) {
				(data.get('includedAliasesIds') as FormArray).removeAt(index);
			}
		});

		const value = [...data.get('includedAliasesIds').value];
		data.patchValue({ isIncluded: value.length > 0 });

		if (this.isConfigurationView) {
			this.triggerSaveForm();
		}
	}

	public onSelectAllClick(): void {
		this.forms.controls.forEach(form => {
			var resourceTypeId = form.get('resourceTypeId').value;
			if (!resourceTypeId) {
				form.patchValue({ isIncluded: true });
			} else {
				this.onMultipleAliasesClick(form as FormGroup);

				this.onSelectAliasesAllClick(form as FormGroup);

				this.resetAliases();
			}
		});

		if (this.isConfigurationView) {
			this.triggerSaveForm();
		}
	}

	public onSelectPriorClick(): void {
		this.isLoading = true;
		this.digitalLogService.getOnDemandDLFormsPriorInspection(TokenHelper.getToken(this.activatedRoute)).subscribe({
			next: (data: any) => {
				this.onClearSelectionClick();
				this.isLoading = false;
				this.forms.controls.forEach(form => {
					var resourceTypeId = form.get('resourceTypeId').value;
					if (!resourceTypeId) {
						var digitalLogFormId = form.get('digitalLogFormId').value;
						var priorValues = data.find(r => r.digitalLogFormId == digitalLogFormId);
						if (priorValues?.digitalLogFormAliases.length == 1
							&& !priorValues?.digitalLogFormAliases[0]) {
							form.patchValue({ isIncluded: true });
						}
					} else {
						this.onMultipleAliasesClick(form as FormGroup);

						this.applySelectAliasesPriorClick(form, data);

						this.resetAliases();
					}
				});
			},
			error: error => {
				ToastService.pushToast({
					icon: 'error',
					text: 'Could not load prior aliases. Try again!',
					type: ToastType.Error
				} as Toast);
				this.isLoading = false;
			}
		});
	}

	public onClearSelectionClick(): void {
		this.forms.controls.forEach(form => {
			(form.get('includedAliasesIds') as FormArray).clear();
			form.get('isIncluded').patchValue(false);
		});

		if (this.isConfigurationView) {
			this.triggerSaveForm();
		}
	}

	public onCancelEditAsset() {
		this.editAssetFormGroup.reset();
		this.editAssetModal.hide();
	}

	public getAssetNameErrorMessage() {
		if (this.name.hasError('required')) {
			return 'Name is required.'
		}
		if (this.name.hasError('minlength')) {
			return 'Name must be at least 2 characters long.'
		}
		if (this.name.hasError('duplicateName')) {
			return 'An alias with the same name, already exists.'
		}

		return '';
	}

	public onSaveAsset() {
		if (this.editAssetModal && this.editAssetModal.data) {
			this.isLoading = true;

			const asset = this.editAssetModal.data;
			const formAsset = this.editAssetFormGroup.value;
			if (!!formAsset && formAsset.subType >= 0) {
				const idx = this.assetSubTypes.findIndex(x => x.id === formAsset?.subType);
				asset.resourceSubTypeId = formAsset?.subType !== 0 ? formAsset.subType : null;
				asset.resourceSubType = formAsset?.subType !== 0 ? this.assetSubTypes[idx]?.name : null;
			}

			asset.alias = formAsset.name;
			asset.aliasIdentifier = formAsset.identifier;
			asset.resourceType = this.selectedOnDemandForm?.resourceType;
			asset.make = formAsset.make;
			asset.model = formAsset.model;
			asset.description = formAsset.description;

			asset.attributes = (this.dynAttributes ?? []).map(d => {
				const ctrl = this.editAssetFormGroup.get(d.definition.formControlName);
				let v = ctrl ? ctrl.value : null;
				
				if (d.definition.attributeTypeId === AttributeType.MultiSelect) {
					v = Array.isArray(v) ? v : (v != null ? [v] : []);
				}
				if (d.definition.attributeTypeId === AttributeType.Date) {
					if (v instanceof Date) {
						v = this.toMMDDYYYY(v);
					} else if (v) {
						const dt = new Date(v);
						v = isNaN(dt.getTime()) ? null : this.toMMDDYYYY(dt);
					} else {
						v = null;
					}
				}
				return {
					attributeDefinitionId: d.definition.id,
					value: v
				};
			});
			this.saveAsset(asset);
		}
	}

	private saveAsset(asset: DigitalLogFormAlias, isChangeAssetStatus = false, data = null) {
		GlobalSettingsService.isSecondaryLoaderLoading = true;
		this.digitalLogService
			.saveAsset(TokenHelper.getToken(this.activatedRoute), asset, isChangeAssetStatus ? false : undefined)
			.subscribe(res => {
				if (asset.accountSiteResourceId) {
					const existingAsset = this.digitalLogFormAliases.find(x => x.accountSiteResourceId === res.accountSiteResourceId);
					if (existingAsset) {
						existingAsset.alias = res.alias;
						existingAsset.aliasIdentifier = res.aliasIdentifier;
						existingAsset.isActive = res.isActive;
						existingAsset.make = res.make;
						existingAsset.model = res.model;
						existingAsset.description = res.description;
						existingAsset.attributes = asset.attributes ?? existingAsset.attributes;
					}

					const assetFormItem = this.aliases.controls.find(x => x.value.accountSiteResourceId === res.accountSiteResourceId);
					if (assetFormItem) {
						assetFormItem.patchValue({ alias: res.alias, aliasIdentifier: res.aliasIdentifier, isActive: res.isActive, resourceSubType: res?.resourceSubType, resourceSubTypeId: res?.resourceSubTypeId, make: res?.make, model: res?.model, description: res?.description });
					}

					if (isChangeAssetStatus) {
						if (res.isActive) {
							assetFormItem.get('alias').enable();
							assetFormItem.get('aliasIdentifier').enable();
							assetFormItem.get('isIncluded').enable();

						} else {
							assetFormItem.patchValue({ isIncluded: false });
							const idx = data.get('includedAliasesIds').value.findIndex(id => id === res.accountSiteResourceId);
							(data.get('includedAliasesIds') as FormArray).removeAt(idx);
							assetFormItem.get('isIncluded').disable();
						}

						const value = [...data.get('includedAliasesIds').value];
						data.patchValue({ isIncluded: value.length > 0 });
					}

				}
				else {
					this.crtExistsSameAssetType = this.crtExistsSameAssetType || (res.accountSiteResourceId != null && this.crtAccountSiteResourceId == res.accountSiteResourceId);

					const newAlias = { ...res, attributes: asset.attributes ?? [] };

					const alias = this.formBuilder.group({
						accountSiteResourceId: [res.accountSiteResourceId],
						isIncluded: new FormControl({ value: false, disabled: (this.crtExistsSameAssetType && this.crtAccountSiteResourceId != res?.accountSiteResourceId) }),
						alias: [res.alias, Validators.maxLength(10)],
						lastUpdatedOn: [null],
						isActive: [true],
						aliasIdentifier: [res.aliasIdentifier],
						canBeIncluded: [!this.crtExistsSameAssetType || this.crtAccountSiteResourceId === res.accountSiteResourceId],
						resourceTypeId: [res.resourceTypeId],
						resourceSubType: [res.resourceSubType],
						resourceSubTypeId: [res.resourceSubTypeId],
						make: [res.make ?? null],
						model: [res.model ?? null],
						description: [res.description ?? null]
					});

					this.aliases.push(alias);
					this.digitalLogFormAliases.push(newAlias);
				}
				this.isLoading = false;
				GlobalSettingsService.isSecondaryLoaderLoading = false;
				if (!isChangeAssetStatus) {
					this.onCancelEditAsset();
				}
				
			}, error => {
				ToastService.pushToast({
					icon: 'error',
					text: 'Could not add Alias. Try again!',
					type: ToastType.Error
				} as Toast);
				this.isLoading = false;
				GlobalSettingsService.isSecondaryLoaderLoading = false;
			});
	}

	private checkAssetDuplicateName() {
		return control => {
			const name = control.value;
			let isDuplicate = false;
			let currentAssetId = null;

			if (this.editAssetModal && this.editAssetModal.data) {
				currentAssetId = this.editAssetModal.data.accountSiteResourceId;
			}

			if (this.digitalLogFormAliases && name &&
				this.digitalLogFormAliases.some(x => x.alias.toLocaleLowerCase() === name.toLocaleLowerCase() && x.accountSiteResourceId !== currentAssetId)
			) {
				isDuplicate = true;
			}

			return isDuplicate ? { duplicateName: { value: name } } : null;
		}
	}

	private getAssetsubTypes(resourceTypeId: number): void {
		if(!resourceTypeId) return;
		this.isLoadingAssetSubTypes = true;
		this.siteService.getAssetSubTypes(resourceTypeId).subscribe(res => {
			this.assetSubTypes = res;
			if (res && res.length) {
				this.assetSubTypes.unshift({ id: 0, name: '(- n/a -)' });
			}
			this.isLoadingAssetSubTypes = false;
		}, error => {
			ToastService.pushToast({
				icon: 'error',
				text: 'Could not load asset subtypes. Try again!',
				type: ToastType.Error
			} as Toast);
			this.isLoadingAssetSubTypes = false;
		});
	}
	public onGroupClick(): void {
		this.groupListModal.open({ data: this.digitalLogProfileGroups });
	}
	public onGroupSelectorModalClose(): void {
		this.groupListModal.hide();
	}
	public onSelectProfileGroup(group: DigitalLogProfileGroup): void {
		this.selectedProfileGroup = group;
		this.isLoading = true;

		// reload profile forms
		// if new or default group selected, reset profiles otherwise load profiles from the back end
		if (this.selectedProfileGroup.digitalLogProfileGroupId <= this.defaultWorkProfile.digitalLogProfileGroupId) {
			this.configurationProfile = [];
			this.loadOnDemandForms(this.onDemandForms);
			this.groupListModal.hide();
			this.isLoading = false;
			return;
		}

		this.digitalLogService.getConfigurationProfile(TokenHelper.getToken(this.activatedRoute), this.selectedProfileGroup.digitalLogProfileGroupId).subscribe(res => {
			this.configurationProfile = res;
			this.loadOnDemandForms(this.onDemandForms);
			this.groupListModal.hide();
			this.isLoading = false;
		});
	}
	public getDigitalLogProfileGroups(): void {
		this.isLoading = true;
		this.digitalLogService.getProfileGroups(TokenHelper.getToken(this.activatedRoute)).subscribe(res => {
			this.digitalLogProfileGroups = res;
			this.isLoading = false;
		}, error => {
			ToastService.pushToast({
				icon: 'error',
				text: 'Could not get profile groups. Try again!',
				type: ToastType.Error
			} as Toast);
			this.isLoading = false;
		});
	}
	public onDeleteGroup(group: DigitalLogProfileGroup): void {
		// if the last group, do not allow deletion, open delete prevention modal
		if (this.digitalLogProfileGroups.length === 1) {
			this.deleteLastGroupModal.open(group);
		} else {
			this.deleteGroupConfirmationModal.open(group);
		}
	}
	public onDeleteGroupConfirmed(group: DigitalLogProfileGroup): void {
		if (group.digitalLogProfileGroupId <= 0) {
			this.digitalLogProfileGroups = this.digitalLogProfileGroups.filter(g => g.digitalLogProfileGroupId !== group.digitalLogProfileGroupId);

			// if there are no more groups, select the default work profile and reset configurations
			if (this.digitalLogProfileGroups.length === 0) {
				this.digitalLogProfileGroups.push(this.defaultWorkProfile);
				this.configurationProfile = [];
				this.selectedProfileGroup = this.defaultWorkProfile;
				this.onSelectProfileGroup(this.selectedProfileGroup);
			}
			else if (this.selectedProfileGroup.digitalLogProfileGroupId === group.digitalLogProfileGroupId) {
				this.selectedProfileGroup = this.digitalLogProfileGroups[0];
				this.onSelectProfileGroup(this.selectedProfileGroup);
			}

			this.deleteGroupConfirmationModal.hide();
			return;
		}

		this.digitalLogService.deleteProfileGroup(TokenHelper.getToken(this.activatedRoute), group.digitalLogProfileGroupId).subscribe(res => {
			ToastService.pushToast({
				icon: 'check_circle',
				text: 'Group deleted successfully!',
				type: ToastType.Success
			} as Toast);
			this.digitalLogProfileGroups = this.digitalLogProfileGroups.filter(g => g.digitalLogProfileGroupId !== group.digitalLogProfileGroupId);

			if (this.selectedProfileGroup.digitalLogProfileGroupId === group.digitalLogProfileGroupId) {
				this.selectedProfileGroup = this.digitalLogProfileGroups.length > 0 
				? this.digitalLogProfileGroups[0] : this.defaultWorkProfile;
				this.onSelectProfileGroup(this.selectedProfileGroup);
			}
			this.deleteGroupConfirmationModal.hide();
		}, error => {
			ToastService.pushToast({
				icon: 'error',
				text: 'Could not delete group. Try again!',
				type: ToastType.Error
			} as Toast);
		});
	}
	public onEditGroupName(group: DigitalLogProfileGroup): void {
		this.groupForm.patchValue({
			name: group.name
		});
		this.editGroupNameModal.open(group);
	}
	public onGroupNameModalClose(): void {
		this.editGroupNameModal.hide();
	}
	public onEditGroupNameConfirmed(group: DigitalLogProfileGroup): void {
		this.isLoading = true;
		const model = this.groupForm.getRawValue();
		const groupName = model.name?.trim();

		this.digitalLogProfileGroups.find(g => g.digitalLogProfileGroupId === group.digitalLogProfileGroupId).name = groupName;

		if (group.digitalLogProfileGroupId === this.defaultWorkProfile.digitalLogProfileGroupId) {
			this.editGroupNameModal.hide();
			this.isLoading = false;
			return;
		}

		if (group.digitalLogProfileGroupId === this.selectedProfileGroup.digitalLogProfileGroupId) {
			this.selectedProfileGroup.name = groupName;
		}

		this.digitalLogService.updateProfileGroupName(TokenHelper.getToken(this.activatedRoute), { ...group, name: groupName }).subscribe(res => {
			ToastService.pushToast({
				icon: 'check_circle',
				text: 'Group name updated successfully!',
				type: ToastType.Success
			} as Toast);

			this.editGroupNameModal.hide();
			this.isLoading = false;
		}, error => {
			ToastService.pushToast({
				icon: 'error',
				text: 'Could not update group name. Try again!',
				type: ToastType.Error
			} as Toast);
			this.isLoading = false;
		});
	}
	private checkGroupDuplicateName() {
		return control => {
			const name = control.value;
			let isDuplicate = false;
			let currentGroupId = null;

			if (this.editGroupNameModal && this.editGroupNameModal.data) {
				currentGroupId = this.editGroupNameModal.data.digitalLogProfileGroupId;
			}

			if (this.digitalLogProfileGroups && name &&
				this.digitalLogProfileGroups.some(x => x.name.toLocaleLowerCase() === name.toLocaleLowerCase() && x.digitalLogProfileGroupId !== currentGroupId)
			) {
				isDuplicate = true;
			}

			return isDuplicate ? { duplicateName: { value: name } } : null;
		}
	}

	public getGroupNameErrorMessage(nameControl: any) {
		if (nameControl.hasError('required')) {
			return 'Name is required.'
		}
		if (nameControl.hasError('minlength')) {
			return 'Name must be at least 2 characters long.'
		}
		if (nameControl.hasError('duplicateName')) {
			return 'A group with the same name, already exists.'
		}

		return '';
	}
	public IsEditGroupUnchanged(group: DigitalLogProfileGroup): boolean {
		if(this.groupForm.pristine) {
			return true;
		}

		return this.groupForm.get('name').value?.trim() === group.name;
	}

	private toMMDDYYYY(d: Date): string | null {
		if (!(d instanceof Date) || isNaN(d.getTime())) return null;
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		const yyyy = d.getFullYear();
		return `${mm}/${dd}/${yyyy}`;
	}

	private prefillAttrControls(attrs: any[]) {
		if (!attrs?.length || !this.dynAttributes?.length) return;

		const toSingle = (x: any) => Array.isArray(x) ? (x.length ? x[x.length - 1] : null) : x;
		const toArray = (x: any) => Array.isArray(x) ? x : (x != null ? [x] : []);

		for (const d of this.dynAttributes) {
			const a = attrs.find(x => x.attributeDefinitionId === d.definition.id);
			if (!a) continue;

			const ctrl = this.editAssetFormGroup.get(d.definition.formControlName);
			if (!ctrl) continue;

			const v = a.value;

			switch (d.definition.attributeTypeId) {
				case AttributeType.Date:
				case AttributeType.DateTime: {
					const sv = toSingle(v);
					ctrl.setValue(sv ? new Date(sv) : null);
					break;
				}
				case AttributeType.Time:
					ctrl.setValue(v ?? null);
					break;

				case AttributeType.Select:
					ctrl.setValue(toSingle(v) ?? null);
					break;

				case AttributeType.MultiSelect:
					ctrl.setValue(toArray(v));
					break;

				case AttributeType.Checkbox: {
					const sv = toSingle(v);
					ctrl.setValue(sv === true || sv === 1 || sv === '1');
					break;
				}

				default:
					ctrl.setValue(toSingle(v) ?? null);
					break;
			}
		}
	}

	onAddGroupName(): void {
		this.addGroupForm.reset();
		this.addGroupModal.open(this.defaultWorkProfile);
	}

	public onAddGroupConfirmed(): void {
		if (this.addGroupForm.valid) {
			const model = this.addGroupForm.getRawValue();
			const groupName = model.name?.trim();

			this.isLoading = true;
			this.digitalLogService.createProfileGroup(TokenHelper.getToken(this.activatedRoute), { name: groupName }).subscribe({
				next: res => {
					ToastService.pushToast({
						icon: 'check_circle',
						text: 'Group created successfully!',
						type: ToastType.Success
					} as Toast);
				const newGroup: DigitalLogProfileGroup = { 
					digitalLogProfileGroupId: res, name: groupName };
				this.selectedProfileGroup = newGroup;
				this.digitalLogProfileGroups.push(newGroup);
				// sort groups by name
				this.digitalLogProfileGroups = this.digitalLogProfileGroups.sort((a, b) => a.name.localeCompare(b.name));
				// remove default work profile from the list if exists
				this.digitalLogProfileGroups = this.digitalLogProfileGroups.filter(g => g.digitalLogProfileGroupId !== this.defaultWorkProfile.digitalLogProfileGroupId);

				// reset and refresh the configuration profile
				this.configurationProfile = [];
				this.loadOnDemandForms(this.onDemandForms);

				this.addGroupForm.reset();
				this.addGroupModal.hide();
				this.groupListModal.hide();
				this.isLoading = false;
			},
				error: () => {
					ToastService.pushToast({
						icon: 'error',
						text: 'Could not create group. Try again!',
						type: ToastType.Error
						} as Toast);
						this.isLoading = false;
					}
			});

		}
	}

	triggerSaveForm() {
		this.saveFormSubject.next();
	}
	buildConfiguration(formValue: any) {
		if (!formValue || !this.selectedProfileGroup) {
			return [];
		}
		return (formValue as JobDigitalLogOnDemandForm[])
			.filter(x => x.isIncluded)
			.map(form => {
				return <ConfigurationProfile>{
					digitalLogFormId: form.digitalLogFormId,
					groupId: this.selectedProfileGroup.digitalLogProfileGroupId,
					accountSiteResourceIds: form.includedAliasesIds
				}
			});
	}
}
